import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Productions", href: "#productions" },
  { label: "Films", to: "/videos" },
  { label: "Inspiration", to: "/inspiration" },
  { label: "Gallery", to: "/gallery" },
  { label: "Story", to: "/share" },
  { label: "Shop", to: "/shop" },
  { label: "Articles", to: "/articles" },
  { label: "Journal", href: "#journal" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = loc.pathname === "/";

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-500 ${
        scrolled ? "bg-imi-bg/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between gap-6">
        <a href="#home" data-testid="logo-link" className="flex items-center gap-4 group shrink-0">
          <img src="/imi-logo.jpg" alt="Inside Mental Injustice" className="h-16 w-16 md:h-20 md:w-20 object-cover rounded-sm border border-imi-border shadow-[0_2px_20px_rgba(212,175,90,0.18)] group-hover:shadow-[0_2px_32px_rgba(212,175,90,0.42)] transition-shadow" />
          <div className="leading-none whitespace-nowrap">
            <div className="font-serif text-imi-goldlight text-xl md:text-2xl tracking-wide">IMI</div>
            <div className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-imi-text/50 mt-1.5">Inside Mental Injustice</div>
          </div>
        </a>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) =>
            n.to ? (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-[13px] uppercase tracking-[0.18em] text-imi-text/60 hover:text-imi-gold transition-colors"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.label}
                href={onHome ? n.href : `/${n.href}`}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-[13px] uppercase tracking-[0.18em] text-imi-text/60 hover:text-imi-gold transition-colors"
              >
                {n.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/share"
            data-testid="header-share-btn"
            className="hidden md:inline-flex items-center bg-imi-gold text-imi-bg text-[12px] uppercase tracking-[0.18em] px-5 py-3 hover:bg-imi-goldlight transition-colors font-semibold"
          >
            Share Your Story
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="xl:hidden text-imi-text"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-imi-bg/95 backdrop-blur-xl border-t border-white/5 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) =>
                n.to ? (
                  <Link key={n.label} to={n.to} onClick={() => setOpen(false)}
                    className="text-sm uppercase tracking-[0.18em] text-imi-text/70 hover:text-imi-gold transition-colors py-1">
                    {n.label}
                  </Link>
                ) : (
                  <a key={n.label} href={onHome ? n.href : `/${n.href}`} onClick={() => setOpen(false)}
                    className="text-sm uppercase tracking-[0.18em] text-imi-text/70 hover:text-imi-gold transition-colors py-1">
                    {n.label}
                  </a>
                )
              )}
              <Link to="/share" onClick={() => setOpen(false)} className="mt-2 inline-flex items-center justify-center bg-imi-gold text-imi-bg text-xs uppercase tracking-[0.18em] px-5 py-3 font-semibold">
                Share Your Story
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
